import { useCallback, useEffect, useRef, useState } from 'react';
import { createHitboxPath, defaultHitboxStyles, getHitboxStyles } from '../utils/hitbox';
import { getBoundaryDimensions, getSecondaryMenuWindowDimensions } from '../utils/secondaryMenuRepositioning';
import useIsVerticalNavPinned from '../../../hooks/useIsVerticalNavPinned';
const useVerticalNavSecondaryMenu = ({
  primaryNavItemRef,
  isSecondaryMenuOpen
}) => {
  const [hitboxPath, setHitboxPath] = useState('');
  const hitboxStyles = useRef(defaultHitboxStyles);
  const secondaryMenu = useRef(null);
  const isVerticalNavPinned = useIsVerticalNavPinned();
  const topOffset = isVerticalNavPinned ? 0 : -42; // account for the menu heading

  const updateSecondaryMenuPositioningStyles = useCallback(top => {
    if (primaryNavItemRef && isSecondaryMenuOpen && secondaryMenu.current) {
      secondaryMenu.current.style.transform = `translate(0px, ${top}px)`;
      setHitboxPath(createHitboxPath({
        primaryNavItemRef,
        secondaryMenuRef: secondaryMenu.current
      }));
    }
  }, [isSecondaryMenuOpen, primaryNavItemRef]);
  const verticallyRepositionSecondaryNavMenu = useCallback(() => {
    if (!primaryNavItemRef.current || !secondaryMenu.current) {
      return;
    }
    const primaryNavItem = primaryNavItemRef.current.getBoundingClientRect();
    const boundary = getBoundaryDimensions();
    const menu = getSecondaryMenuWindowDimensions({
      secondaryMenuRef: secondaryMenu
    });
    if (primaryNavItem.top + topOffset < boundary.top || menu.height > boundary.height) {
      const topOverflow = boundary.top - (primaryNavItem.top + topOffset);
      return updateSecondaryMenuPositioningStyles(primaryNavItem.top + topOffset + topOverflow);
    }
    if (primaryNavItem.top + menu.height + topOffset > boundary.bottom) {
      const bottomOverflow = primaryNavItem.top + topOffset + menu.height - boundary.bottom;
      return updateSecondaryMenuPositioningStyles(primaryNavItem.top + topOffset - bottomOverflow);
    }
    return updateSecondaryMenuPositioningStyles(primaryNavItem.top + topOffset);
  }, [primaryNavItemRef, topOffset, updateSecondaryMenuPositioningStyles]);
  const setSecondaryMenuItemRef = useCallback(secondaryMenuRef => {
    secondaryMenu.current = secondaryMenuRef;
    verticallyRepositionSecondaryNavMenu();
    setHitboxPath(createHitboxPath({
      primaryNavItemRef,
      secondaryMenuRef
    }));
    hitboxStyles.current = getHitboxStyles({
      primaryNavItemRef,
      secondaryMenuRef
    });
  }, [primaryNavItemRef, verticallyRepositionSecondaryNavMenu]);
  useEffect(() => {
    const repositionMenu = () => window.requestAnimationFrame(verticallyRepositionSecondaryNavMenu);
    window.addEventListener('resize', repositionMenu);
    return () => {
      window.removeEventListener('resize', repositionMenu);
    };
  }, [verticallyRepositionSecondaryNavMenu]);
  return {
    hitboxPath,
    setSecondaryMenuItemRef,
    hitboxStyles
  };
};
export default useVerticalNavSecondaryMenu;